<template>
  <div class="container">
    <p style="color: #595959">
      Gestiona los contactos de tus campañas. Puedes ver, editar información y realizar acciones
      individuales como llamadas.
      <a :href="DOCS_URL" target="_blank" rel="noopener noreferrer">Click aquí </a> para conocer
      más.
    </p>
    <contact-table-controls
      ref="controls"
      :total="contactsInfo.count"
      :selectedRows="selectedTableRowKeys"
      @onOpenAdvanceFilter="handleOpenAdvanceFilter"
      @open-column-filter-drawer="handleOpenColumnFilterDrawer"
      :typificationType="typificationType"
    ></contact-table-controls>

    <contact-table
      @onClientSelectId="handleSelectedContactId"
      :columns="columnsItemsVisibles"
      :contactsInfo="contactsInfo"
      :item-id="selectedContactId"
    />

    <contact-detail-drawer
      :visible="isContactDetailVisible"
      @onCloseDrawer="handleCloseDrawer"
      :item-id="selectedContactId"
      :contactsInfo="contactsInfo"
      @update="handleUpdate"
    />

    <filter-advanced-drawer
      v-model:visible="isFilterAdvancedVisible"
      @onTypificationType="handleTypificationType"
    />

    <column-filter
      v-if="columnFilterDrawerVisible"
      v-model:visible="columnFilterDrawerVisible"
      v-model:value="columnsKeysVisibles"
      :columns="columnsPreferences"
      :titleColumns="titleColumns"
      scope="contacts"
      @apply-filters="handleApplyColumnFilters"
      @clear-filters="handleClearColumnFilters"
      @close-drawer="handleCloseDrawer"
    />
  </div>
</template>

<script>
import { ref, provide, onMounted, computed } from "vue";

import { ClientInformationTabsKey } from "@/app/contacts/utils/enums";

import ContactDetailDrawer from "../drawers/ContactDetailDrawer.vue";
import ContactTable from "../tables/ContactTable.vue";
import ContactTableControls from "../tables/ContactTableControls.vue";
import FilterAdvancedDrawer from "../drawers/FilterAdvancedDrawer.vue";
import { fetchTablePreferences } from "@/app/shared/services";
import ColumnFilter from "@/app/ticketsManager/components/drawers/ColumnFilter.vue";
import { getWalletsTableColumns } from "@/app/contacts/constants";
import { useStore } from "vuex";

/* eslint-disable vue/no-unused-components */
export default {
  components: {
    ContactTable,
    ContactTableControls,
    ContactDetailDrawer,
    FilterAdvancedDrawer,
    ColumnFilter,
  },
  setup() {
    const loadingTableData = ref(false);
    const selectedTabId = ref(ClientInformationTabsKey.PersonalData);
    const titleColumns =
      "Algunas columnas, como el Cod Contacto, Nombres y apellidos, Empresa, Campaña y Usuario asignado son esenciales y no pueden ocultarse.";

    provide("loadingTableData", loadingTableData);
    provide("selectedTabId", selectedTabId);

    const columnFilterDrawerVisible = ref(false);

    const handleOpenColumnFilterDrawer = () => {
      columnFilterDrawerVisible.value = true;
    };

    const store = useStore();
    const user = computed(() => store.getters["getDataUser"]);
    const contactsTableColumns = getWalletsTableColumns(user.value?.customer?.enable_bases);

    /**** MASSIVE SELECT ****/
    const selectedTableRowKeys = ref([]);
    const selectedHubspotExportRowKeys = ref([]);
    const selectedHubspotResyncRowKeys = ref([]);
    const rowsListMassive = ref([]);
    const selectedAll = ref(false);

    const countStatusSelected = ref({ actives: 0, inactives: 0 });

    provide("countStatusSelected", countStatusSelected);
    provide("selectedTableRowKeys", selectedTableRowKeys);
    provide("selectedHubspotExportRowKeys", selectedHubspotExportRowKeys);
    provide("selectedHubspotResyncRowKeys", selectedHubspotResyncRowKeys);
    provide("rowsListMassive", rowsListMassive);
    provide("selectedAll", selectedAll);

    provide(
      "updateSelectedTableRowKeys",
      (newSelectedRows) => (selectedTableRowKeys.value = newSelectedRows)
    );

    /**** END MASSIVE SELECT ****/

    /**** COLUMNS FILTERS ****/
    const columnsPreferences = ref([]);
    const columnsKeysVisibles = ref(
      contactsTableColumns.filter((el) => el.code != "actions").map((el) => el.code)
    );
    const columnsItemsVisibles = computed(() =>
      contactsTableColumns.filter(
        (el) => columnsKeysVisibles.value.includes(el.code) || el.code == "actions"
      )
    );

    const EXCLUDED_CODES = new Set(['queues']);
    const norm = (v) => String(v ?? '').trim().toLowerCase();
    const defaultVisibleCodes = contactsTableColumns
      .filter((col) => {
        const code = norm(col?.code);
        return code && code !== "actions" && !EXCLUDED_CODES.has(code);
      })
      .map((col) => col.code);

    const handleFetchTablePreferences = async () => {
      const prefs = await fetchTablePreferences("contacts/");
      columnsPreferences.value = (prefs ?? []).filter(
        (p) => !EXCLUDED_CODES.has(norm(p?.code))
      );
      console.log("COLUMNS KEYS VISIBLES AFTER FETCH:", columnsPreferences.value);
      // 1) Códigos asignados, excluyendo los no permitidos
      const assignedCodes = new Set(
        columnsPreferences.value
          .filter((p) => p?.is_assigned && norm(p?.code) !== "actions" && !EXCLUDED_CODES.has(norm(p?.code)))
          .map((p) => norm(p.code))
      );

      // 2) Construye columnsKeysVisibles solo con los asignados válidos
      columnsKeysVisibles.value = contactsTableColumns
        .filter((col) => {
          const code = norm(col?.code);
          return code && code !== "actions" && !EXCLUDED_CODES.has(code) && assignedCodes.has(code);
        })
        .map((col) => col.code);

      if (columnsKeysVisibles.value.length === 0) {
        columnsKeysVisibles.value = [...defaultVisibleCodes];
      }
    };
    /**** END COLUMNS FILTERS ****/

    onMounted(async () => {
      await handleFetchTablePreferences();
    });

    return {
      ClientInformationTabsKey,
      selectedTabId,
      selectedTableRowKeys,
      selectedHubspotExportRowKeys,
      selectedHubspotResyncRowKeys,
      handleOpenColumnFilterDrawer,
      columnFilterDrawerVisible,
      columnsKeysVisibles,
      columnsItemsVisibles,
      handleFetchTablePreferences,
      contactsTableColumns,
      columnsPreferences,
      titleColumns,
    };
  },
  data() {
    const DOCS_URL = `${import.meta.env.VITE_WEB_DOC}/crm/contactos-1/`;

    return {
      isContactDetailVisible: false,
      isFilterAdvancedVisible: false,
      selectedContactId: null,
      selectedButtonFilter: null,
      search: "",
      entity: 0,
      portfolio: 0,
      strategyType: 0,
      typificationType: 1,
      DOCS_URL,
    };
  },
  computed: {
    contactsInfo() {
      const contacts = this.$store.getters["getContactsInfoTable"];
      console.log("[TEST LOGS] Contacts list: ", contacts)
      return this.$store.getters["getContactsInfoTable"];
    },
    filterBasic() {
      return this.$store.getters["getBasicFilters"];
    },
  },
  sockets: {
    refreshNotification(data) {
      if (data.extra_info == "hubspot_contacts_export") {
        this.handleUpdate();
      }
    },
  },
  methods: {
    handleCloseDrawer() {
      this.isContactDetailVisible = false;
      this.selectedContactId = null;
    },
    handleCloseFilterAdvancedDrawer() {
      this.isFilterAdvancedVisible = false;
    },
    handleSelectedContactId({ clientId, tab }) {
      this.selectedTabId = tab;
      this.selectedContactId = clientId;
      this.isContactDetailVisible = true;
    },
    handleOpenAdvanceFilter() {
      this.isFilterAdvancedVisible = true;
    },
    handleTypificationType($event) {
      this.typificationType = $event;
    },
    handleUpdate() {
      this.$refs.controls.handleSearch();
    },
  },
};
</script>

<style lang="sass" scoped>
.container
  padding: 12px 20px

.controls
  margin-bottom: 8px
</style>
